/**
 * Port of `repos/opencode/packages/ui/src/components/accordion.tsx`.
 *
 * Kobalte -> Radix. Radix discriminates its root props on `type` ("single" | "multiple")
 * where Kobalte takes a `multiple` boolean; upstream's callers pass `multiple`, so both
 * forms are accepted and normalised. The branch below is what keeps the discriminated
 * union intact — spreading a widened `type` into the root does not typecheck.
 */

import * as RadixAccordion from "@radix-ui/react-accordion";
import type { ComponentPropsWithoutRef } from "react";

type RootProps = ComponentPropsWithoutRef<typeof RadixAccordion.Root>;
type SingleProps = Extract<RootProps, { type: "single" }>;
type MultipleProps = Extract<RootProps, { type: "multiple" }>;

export type AccordionProps =
  | (Omit<SingleProps, "type"> & { type?: "single"; multiple?: false })
  | (Omit<MultipleProps, "type"> & { type: "multiple"; multiple?: boolean })
  | (Omit<MultipleProps, "type"> & { type?: undefined; multiple: true });

function AccordionRoot({ type, multiple, ...props }: AccordionProps) {
  if (type === "multiple" || multiple) {
    return (
      <RadixAccordion.Root
        data-component="accordion"
        {...(props as Omit<MultipleProps, "type">)}
        type="multiple"
      />
    );
  }
  return (
    <RadixAccordion.Root
      data-component="accordion"
      {...(props as Omit<SingleProps, "type">)}
      type="single"
    />
  );
}

function AccordionItem(props: ComponentPropsWithoutRef<typeof RadixAccordion.Item>) {
  return <RadixAccordion.Item data-slot="accordion-item" {...props} />;
}

function AccordionHeader(props: ComponentPropsWithoutRef<typeof RadixAccordion.Header>) {
  return <RadixAccordion.Header data-slot="accordion-header" {...props} />;
}

function AccordionTrigger(props: ComponentPropsWithoutRef<typeof RadixAccordion.Trigger>) {
  return <RadixAccordion.Trigger data-slot="accordion-trigger" {...props} />;
}

function AccordionContent(props: ComponentPropsWithoutRef<typeof RadixAccordion.Content>) {
  return <RadixAccordion.Content data-slot="accordion-content" {...props} />;
}

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Header: AccordionHeader,
  Trigger: AccordionTrigger,
  Content: AccordionContent
});
